import type { ReactElement } from 'react';
import { useMemo } from 'react';

import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaImportedEventHit } from '#/modules/search/algolia/types/SearchAlgoliaImportedEventHit';
import { SearchHitImage } from '#/modules/search/components/Hit/Image/SearchHitImage';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from '#/modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from '#/modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import { getImportedListingUrl } from '#/modules/search/helpers/getImportedListingUrl';
import { getSearchHitElementId } from '#/modules/search/utils/getSearchHitElementId';
import type { DatetimeString } from '#/types/TypedDateString';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';

import { SearchImportedHitInfo } from './SearchImportedHitInfo';

type Props = {
  hit: SearchAlgoliaImportedEventHit;
  trackClick: (() => void) | null;
  showLabel: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  actions: ReactElement | null;
};

export function SearchImportedHit({
  hit,
  trackClick,
  showLabel,
  highlighted,
  application,
  actions,
}: Props) {
  const { i18n } = useI18n();

  const {
    name,
    city,
    state,
    country,
    url,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    orgType,
    source,
    locationType,
    remoteZone,
    logo,
    imageUrl,
    isPostedAnonymously,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteZone, city, country, state],
  );

  const { orgName } = hit;

  return (
    <SearchHitHolder
      id={getSearchHitElementId(hit.objectID)}
      data-qa-id="search-result"
    >
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link',
            href: getImportedListingUrl(url),
            target: '_blank',
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile>
            <SearchHitImage
              isImported
              imageUrl={imageUrl}
              image={null}
              source={source}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchImportedHitInfo
            application={application}
            startDate={startDate}
            startTime={startTime}
            endDate={endDate}
            endTime={endTime}
            timezone={timezone}
            isPostedAnonymously={isPostedAnonymously}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            showLabel={showLabel}
            source={source}
          />
        </SearchHitInteractiveContentWrapper>
      </SearchHitCard>
    </SearchHitHolder>
  );
}
